import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchReport, API_BASE } from "@/lib/api";
import ReportFull, { ReportPreview } from "@/components/report/ReportFull";
import Paywall from "@/components/report/Paywall";
import ClosingStrategyUpsell from "@/components/report/ClosingStrategyUpsell";
import ShareReportModal from "@/components/report/ShareReportModal";
import QuoteRequestModal from "@/components/report/QuoteRequestModal";
import InvestorNotes from "@/components/investor/InvestorNotes";
import InvestorComparisonCTA from "@/components/investor/InvestorComparisonCTA";
import MicroFeedbackBar from "@/components/feedback/MicroFeedbackBar";
import { Download, ArrowLeft, Share2, Wrench, FileText, Layout } from "lucide-react";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";
import useIsMobile from "@/lib/useIsMobile";
import ReportPdfStyledShell from "@/components/report/ReportPdfStyledShell";

export default function ReportView() {
    const { reportId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [shareOpen, setShareOpen] = useState(false);
    const [quoteOpen, setQuoteOpen] = useState(false);
    const [viewMode, setViewMode] = useState("web");  // "web" | "pdf"
    const { user } = useAuth();
    const isMobile = useIsMobile();

    const load = async () => {
        try {
            const r = await fetchReport(reportId);
            setData(r);
            track("report_reopened", { report_id: reportId, owned_tier: r.owned_tier });
        } catch (e) {
            toast.error("Could not load this report.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId]);

    // Poll while server-side generation is in progress
    useEffect(() => {
        if (!data?.report) return;
        if (data.report.status !== "generating") return;
        const t = setInterval(async () => {
            try {
                const r = await fetchReport(reportId);
                setData(r);
                if (r.report?.status !== "generating") {
                    clearInterval(t);
                }
            } catch { /* non-critical */ }
        }, 3500);
        return () => clearInterval(t);
    }, [data, reportId]);

    const onDownloadPdf = () => {
        // Browser will handle the download. Uses cookie auth via the same origin.
        const url = `${API_BASE}/reports/${reportId}/export.pdf`;
        track("report_pdf_downloaded", { report_id: reportId });
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        a.click();
    };

    // v2.2: Manual report regeneration removed to prevent runaway OpenAI charges.
    // Reports regenerate exactly once per purchased tier; Premium re-generates exactly
    // once when a Summary owner upgrades to Closing Strategy. No manual button.

    if (loading) {
        return (
            <PublicLayout>
                <div className="bl-container py-24 text-center text-bl-muted">
                    <div className="bl-spinner mx-auto mb-4" />
                    Loading report…
                </div>
            </PublicLayout>
        );
    }

    if (!data?.report) {
        return (
            <PublicLayout>
                <div className="bl-container py-24">
                    <h1 className="font-display text-3xl text-bl">Report not found</h1>
                    <Link to="/dashboard" className="bl-btn-secondary mt-6">
                        Back to dashboard
                    </Link>
                </div>
            </PublicLayout>
        );
    }

    const { report, owned_tier, is_sample } = data;
    const hasFull = !!report.full_summary && (owned_tier || is_sample);
    const isGenerating = report.status === "generating" && !!owned_tier && !hasFull;
    // Investor-subscription users get full reports auto-granted on upload.
    // While their report is still generating (or briefly after upload, before
    // the entitlement row arrives), suppress the paywall — show generating state instead.
    const isInvestorSub = !!user?.active_subscription;

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container pt-12 pb-6" data-testid="report-page">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <Link
                            to="/dashboard"
                            className="bl-btn-ghost flex items-center gap-2"
                            data-testid="report-back-link"
                        >
                            <ArrowLeft className="w-4 h-4" /> Dashboard
                        </Link>
                        {hasFull && !is_sample && (
                            <div className="flex gap-2 flex-wrap items-center">
                                {/* Web ⇄ PDF view toggle. Desktop PDF mode embeds the
                                    real export.pdf in an iframe; mobile renders a
                                    PDF-styled wrapper around the existing React
                                    content (more reliable than mobile PDF embedding). */}
                                <div
                                    className="inline-flex rounded-full border border-bl bg-bl-surface p-0.5 shadow-sm"
                                    role="tablist"
                                    aria-label="Report view mode"
                                    data-testid="report-view-mode-toggle"
                                >
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={viewMode === "web"}
                                        onClick={() => {
                                            setViewMode("web");
                                            track("report_view_mode_changed", {
                                                report_id: reportId,
                                                mode: "web",
                                            });
                                        }}
                                        className={
                                            "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors " +
                                            (viewMode === "web"
                                                ? "bg-bl-primary text-bl-surface shadow-sm"
                                                : "text-bl-muted hover:text-bl")
                                        }
                                        data-testid="report-view-mode-web"
                                    >
                                        <Layout className="w-3.5 h-3.5" />
                                        Web view
                                    </button>
                                    <button
                                        type="button"
                                        role="tab"
                                        aria-selected={viewMode === "pdf"}
                                        onClick={() => {
                                            setViewMode("pdf");
                                            track("report_view_mode_changed", {
                                                report_id: reportId,
                                                mode: "pdf",
                                                channel: isMobile ? "styled" : "embed",
                                            });
                                        }}
                                        className={
                                            "inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors " +
                                            (viewMode === "pdf"
                                                ? "bg-bl-primary text-bl-surface shadow-sm"
                                                : "text-bl-muted hover:text-bl")
                                        }
                                        data-testid="report-view-mode-pdf"
                                    >
                                        <FileText className="w-3.5 h-3.5" />
                                        PDF view
                                    </button>
                                </div>
                                <button
                                    className="bl-btn-secondary"
                                    onClick={() => setShareOpen(true)}
                                    data-testid="report-share-btn"
                                >
                                    <Share2 className="w-4 h-4" /> Share with my agent
                                </button>
                                <button
                                    className="bl-btn-secondary"
                                    onClick={() => setQuoteOpen(true)}
                                    data-testid="report-quote-btn"
                                >
                                    <Wrench className="w-4 h-4" /> Request quote
                                </button>
                                <button
                                    className="bl-btn-secondary"
                                    onClick={onDownloadPdf}
                                    data-testid="report-pdf-btn"
                                >
                                    <Download className="w-4 h-4" /> Download PDF
                                </button>
                            </div>
                        )}
                        {is_sample && (
                            <button
                                className="bl-btn-secondary"
                                onClick={onDownloadPdf}
                                data-testid="report-pdf-btn"
                            >
                                <Download className="w-4 h-4" /> Download sample PDF
                            </button>
                        )}
                    </div>
                </div>
            </section>

            <section className="bl-container pb-20 space-y-12">
                {isGenerating ? (
                    <div
                        className="bl-card p-10 text-center"
                        data-testid="report-generating"
                    >
                        <div className="bl-spinner mx-auto mb-5" />
                        <h2 className="font-display font-semibold text-2xl text-bl">
                            Generating your full report…
                        </h2>
                        <p className="mt-3 text-bl-muted max-w-md mx-auto leading-relaxed">
                            You can safely leave this page — we'll save the
                            report to your dashboard when it's ready. Large
                            scanned PDFs may take a few minutes.
                        </p>
                        <ol className="mt-5 inline-block text-left space-y-1.5 text-xs text-bl-muted">
                            {[
                                "Extracting inspection findings",
                                "Organizing report sections",
                                "Estimating professional repair exposure",
                                "Preparing your negotiation-ready report",
                            ].map((stage, i) => (
                                <li key={stage} className="flex items-center gap-2">
                                    <span className="font-mono-bl tnum text-bl-primary">0{i + 1}</span>
                                    {stage}
                                </li>
                            ))}
                        </ol>
                    </div>
                ) : hasFull ? (
                    <>
                        {viewMode === "pdf" && !isMobile ? (
                            // Desktop PDF — embed the real export.pdf inline so
                            // the user sees exactly what they'd download.
                            <div
                                className="bl-card overflow-hidden"
                                data-testid="report-pdf-embed"
                                style={{ padding: 0 }}
                            >
                                <iframe
                                    src={`${API_BASE}/reports/${reportId}/export.pdf?inline=1#view=FitH`}
                                    title="Inspection Report PDF"
                                    style={{
                                        width: "100%",
                                        height: "min(80vh, 1100px)",
                                        border: "none",
                                        display: "block",
                                    }}
                                />
                            </div>
                        ) : viewMode === "pdf" ? (
                            // Mobile PDF — same React content, wrapped in a
                            // printed-page look (white pages on warm grey).
                            <ReportPdfStyledShell>
                                <ReportFull
                                    report={report}
                                    ownedTier={owned_tier}
                                    isSample={is_sample}
                                    onGetQuotes={() => setQuoteOpen(true)}
                                />
                            </ReportPdfStyledShell>
                        ) : (
                            <ReportFull
                                report={report}
                                ownedTier={owned_tier}
                                isSample={is_sample}
                                onGetQuotes={() => setQuoteOpen(true)}
                            />
                        )}
                        <ClosingStrategyUpsell
                            uploadId={report.upload_id}
                            ownedTier={owned_tier}
                            reportId={report.report_id}
                            isSample={is_sample}
                        />
                        {/* Phase 2 — Investor private notes + comparison CTA */}
                        <InvestorComparisonCTA reportId={report.report_id} isSample={is_sample} />
                        <InvestorNotes reportId={report.report_id} isSample={is_sample} />
                        {/* Phase 5 — calm post-report micro-feedback */}
                        <MicroFeedbackBar
                            reportId={report.report_id}
                            isSample={is_sample}
                            ownedTier={owned_tier}
                        />
                        {/* Bottom-of-report Get Local Quotes anchor */}
                        {!is_sample && (
                            <div
                                className="bl-card p-6 sm:p-8 md:p-10"
                                data-testid="report-bottom-quotes-cta"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <div>
                                        <div className="bl-eyebrow w-fit">Next step</div>
                                        <h3 className="mt-2 font-display font-semibold text-xl sm:text-2xl text-bl">
                                            Move from insight to action.
                                        </h3>
                                        <p className="mt-2 text-bl-muted text-sm leading-relaxed max-w-xl">
                                            Compare local repair options for the issues you care about — privacy-conscious, optional, on your terms.
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            track("quote_cta_clicked", { source: "report-bottom" });
                                            setQuoteOpen(true);
                                        }}
                                        className="bl-btn-primary whitespace-nowrap"
                                        data-testid="report-bottom-get-quotes-btn"
                                    >
                                        <Wrench className="w-4 h-4" /> Get local quotes
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                ) : isInvestorSub && report.user_id === user?.user_id ? (
                    <div
                        className="bl-card p-10 text-center"
                        data-testid="report-investor-generating"
                    >
                        <div className="bl-spinner mx-auto mb-5" />
                        <h2 className="font-display font-semibold text-2xl text-bl">
                            Preparing your full report…
                        </h2>
                        <p className="mt-3 text-bl-muted max-w-md mx-auto leading-relaxed">
                            Your investor subscription unlocks every upload at the
                            Closing Strategy tier — no checkout needed. This page will
                            refresh automatically.
                        </p>
                    </div>
                ) : (
                    <>
                        <ReportPreview report={report} />
                        <Paywall
                            uploadId={report.upload_id}
                            ownedTier={owned_tier}
                            onSuccess={load}
                        />
                    </>
                )}
            </section>

            {/* Discreet support entry-point — context is auto-attached
               via query params so the user doesn't have to re-explain. */}
            {!is_sample && report?.report_id && (
                <section
                    className="bl-container pb-12"
                    data-testid="report-support-cta"
                >
                    <div className="text-center text-sm text-bl-muted">
                        Something off with this report?{" "}
                        <Link
                            to={`/support?report_id=${report.report_id}${report.upload_id ? `&upload_id=${report.upload_id}` : ""}`}
                            className="text-bl-primary hover:underline"
                            data-testid="report-need-help-link"
                        >
                            Need help with this report?
                        </Link>
                    </div>
                </section>
            )}

            <ShareReportModal
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                reportId={reportId}
                propertyAddress={report?.property_address}
            />
            <QuoteRequestModal
                open={quoteOpen}
                onClose={() => setQuoteOpen(false)}
                report={report}
                reportId={reportId}
                sourceLocation="report-overview"
            />
        </PublicLayout>
    );
}
